import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChatBubbleLeftRightIcon, 
  PaperClipIcon, 
  AtSymbolIcon,
  HashtagIcon,
  CalendarIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import VisibilityIndicator from './VisibilityIndicator';

const SharedNotes = ({ 
  projectId, 
  notes = [], 
  currentUser,
  teamMembers = [],
  onAddNote,
  onUpdateNote,
  onDeleteNote,
  visibility = 'organization'
}) => {
  const [newNote, setNewNote] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [showMentions, setShowMentions] = useState(false);
  const [noteVisibility, setNoteVisibility] = useState(visibility);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (newNote.trim()) {
      onAddNote({
        content: newNote,
        visibility: noteVisibility,
        author: currentUser,
        timestamp: new Date().toISOString(),
        projectId
      });
      setNewNote('');
    }
  };

  const parseContent = (content) => {
    const parts = content.split(/(@\w+|#\w+)/g);
    return parts.map((part, index) => {
      if (part.startsWith('@')) {
        return (
          <span key={index} className="text-brand font-medium hover:underline cursor-pointer">
            {part}
          </span>
        );
      } else if (part.startsWith('#')) {
        return (
          <span key={index} className="text-[#E6A15E] font-medium hover:underline cursor-pointer">
            {part}
          </span>
        );
      }
      return part;
    });
  };

  return (
    <div className="bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#2A2A2A] rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-[#2A2A2A]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-brand to-[#FF9D5C]">
              <ChatBubbleLeftRightIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Team Notes
              </h3>
              <p className="text-xs text-gray-500 dark:text-[#666666]">
                {notes.length} notes • {notes.filter(n => !n.resolved).length} active
              </p>
            </div>
          </div>
          <VisibilityIndicator 
            visibility={noteVisibility}
            onVisibilityChange={setNoteVisibility}
            interactive
            size="sm"
          />
        </div>
      </div>

      {/* Notes List */}
      <div className="max-h-96 overflow-y-auto custom-scrollbar">
        <AnimatePresence>
          {notes.map((note, index) => (
            <motion.div
              key={note.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ delay: index * 0.05 }}
              className="px-6 py-4 border-b border-gray-100 dark:border-[#1A1A1A] hover:bg-gray-50 dark:hover:bg-[#0F0F0F] transition-colors"
            >
              <div className="flex items-start gap-3">
                {/* Author Avatar */}
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand to-[#FF9D5C] flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-black">
                    {note.author?.initials || 'UN'}
                  </span>
                </div>

                {/* Note Content */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {note.author?.name || 'Unknown'}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-[#666666]">
                      {new Date(note.timestamp).toLocaleString()}
                    </span>
                    {note.visibility && (
                      <VisibilityIndicator visibility={note.visibility} size="xs" />
                    )}
                    {note.resolved && (
                      <CheckCircleIcon className="w-4 h-4 text-green-500" />
                    )}
                  </div>
                  <p className="text-sm text-gray-700 dark:text-[#CCCCCC] leading-relaxed">
                    {parseContent(note.content)}
                  </p>

                  {/* Attachments */}
                  {note.attachments && note.attachments.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {note.attachments.map((attachment, i) => (
                        <div
                          key={i}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-[#1A1A1A] rounded-lg text-xs text-gray-600 dark:text-[#999999]"
                        >
                          <PaperClipIcon className="w-3 h-3" />
                          {attachment.name}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="mt-2 flex items-center gap-3">
                    <button className="text-xs text-gray-500 hover:text-brand transition-colors">
                      Reply
                    </button>
                    <button className="text-xs text-gray-500 hover:text-brand transition-colors">
                      Edit
                    </button>
                    {!note.resolved && (
                      <button className="text-xs text-gray-500 hover:text-green-500 transition-colors">
                        Resolve
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* New Note Form */}
      <form onSubmit={handleSubmit} className="p-4 border-t border-gray-200 dark:border-[#2A2A2A]">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand to-[#FF9D5C] flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-bold text-black">
              {currentUser?.initials || 'ME'}
            </span>
          </div>
          
          <div className="flex-1">
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Add a note... Use @ to mention, # for tags"
              className="w-full px-3 py-2 bg-gray-50 dark:bg-[#0F0F0F] border border-gray-200 dark:border-[#2A2A2A] rounded-lg focus:ring-2 focus:ring-brand focus:border-brand outline-none resize-none text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-[#666666]"
              rows={isExpanded ? 3 : 1}
              onFocus={() => setIsExpanded(true)}
              onBlur={() => !newNote && setIsExpanded(false)}
            />
            
            {isExpanded && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-2 flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1A1A1A] transition-colors"
                    onClick={() => setNewNote(newNote + '@')}
                  >
                    <AtSymbolIcon className="w-4 h-4 text-gray-500 dark:text-[#666666]" />
                  </button>
                  <button
                    type="button"
                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1A1A1A] transition-colors"
                    onClick={() => setNewNote(newNote + '#')}
                  >
                    <HashtagIcon className="w-4 h-4 text-gray-500 dark:text-[#666666]" />
                  </button>
                  <button
                    type="button"
                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1A1A1A] transition-colors"
                  >
                    <PaperClipIcon className="w-4 h-4 text-gray-500 dark:text-[#666666]" />
                  </button>
                  <button
                    type="button"
                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1A1A1A] transition-colors"
                  >
                    <CalendarIcon className="w-4 h-4 text-gray-500 dark:text-[#666666]" />
                  </button>
                </div>
                
                <button
                  type="submit"
                  className="px-4 py-1.5 bg-brand hover:bg-brand-hover text-black font-medium rounded-lg transition-colors text-sm"
                  disabled={!newNote.trim()}
                >
                  Add Note
                </button>
              </motion.div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default SharedNotes;